import React, { useState } from "react";
import { T } from "../../app/tokens.js";
import { useOkrs } from "./useOkrs.js";
import { OkrObjective } from "./OkrObjective.jsx";
import { ObjectiveForm, KeyResultForm, InitiativeForm } from "./OkrForms.jsx";
import { OkrDigestView } from "./OkrDigestView.jsx";
import { OkrInitiativeUpdatesModal } from "./OkrInitiativeUpdatesModal.jsx";
import { Plus } from "lucide-react";
import { ErrorBoundary } from "../../components/ErrorBoundary.jsx";

const LENS_OPTIONS = [
  { value: "quarter", label: "Quarter target" },
  { value: "date", label: "Target date" },
  { value: "overall", label: "Overall" },
  { value: "digest", label: "Monthly digest" },
];

export function OkrsPage({ currentUser, currentRole }) {
  const {
    lens,
    handleLensChange,
    year,
    handleYearChange,
    month,
    handleMonthChange,
    data,
    loading,
    error,
    addObjective,
    editObjective,
    addKeyResult,
    editKeyResult,
    addInitiative,
    editInitiative,
    saveInitiativeUpdate
  } = useOkrs("overall");

  const canEdit = currentRole === "admin" || currentRole === "editor";

  const [objModal, setObjModal] = useState({ open: false, data: null });
  const [krModal, setKrModal] = useState({ open: false, data: null, objectiveId: null });
  const [iniModal, setIniModal] = useState({ open: false, data: null, keyResultId: null });
  const [updateModal, setUpdateModal] = useState({ open: false, initiative: null });

  const handleObjSubmit = async (payload) => {
    if (objModal.data) await editObjective(objModal.data.id, payload);
    else await addObjective(payload);
  };

  const handleKrSubmit = async (payload) => {
    if (krModal.data) await editKeyResult(krModal.data.id, payload);
    else await addKeyResult(payload);
  };

  const handleIniSubmit = async (payload) => {
    if (iniModal.data) await editInitiative(iniModal.data.id, payload);
    else await addInitiative(payload);
  };

  if (error) {
    return (
      <div style={{ padding: 32, maxWidth: 1000, margin: "0 auto", color: T.red }}>
        <h3>Failed to load OKRs</h3>
        <p>{error.message}</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div style={{ padding: "32px 40px", maxWidth: 1200, margin: "0 auto", background: T.bg, minHeight: "100vh" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 32 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "0 0 8px" }}>
              <h1 style={{ fontSize: T.fsXl5, fontWeight: T.fwBold, color: T.dark, margin: 0 }}>FY</h1>
              <select
                value={year}
                onChange={e => handleYearChange(Number(e.target.value))}
                style={{
                  fontSize: T.fsXl5, fontWeight: T.fwBold, color: T.dark, margin: 0,
                  background: "transparent", border: "none", outline: "none", cursor: "pointer",
                  appearance: "none", padding: "0 4px", borderBottom: `3px solid ${T.accent}`
                }}
              >
                 {Array.from({ length: 201 }, (_, i) => 2000 + i).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <h1 style={{ fontSize: T.fsXl5, fontWeight: T.fwBold, color: T.dark, margin: 0 }}>OKRs</h1>
            </div>
            <p style={{ fontSize: T.fsBase, color: T.mid, margin: 0 }}>
              Company objectives and deterministic progress rollups.
            </p>
          </div>
          
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ display: "flex", background: T.borderCompact, padding: 2, borderRadius: 6 }}>
              {LENS_OPTIONS.map(opt => {
                const isActive = lens === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleLensChange(opt.value)}
                    style={{
                      padding: "6px 12px",
                      fontSize: T.fsSm, fontWeight: T.fwSemibold,
                      background: isActive ? T.white : "transparent",
                      color: isActive ? T.dark : T.mid,
                      border: "none", borderRadius: 4, cursor: "pointer",
                      boxShadow: isActive ? T.shadowTileXs : "none",
                      transition: "all 0.2s ease"
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            
            {canEdit && (
              <button
                onClick={() => setObjModal({ open: true, data: null })}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 16px", borderRadius: 6,
                  background: T.accent, color: T.white,
                  border: "none", cursor: "pointer",
                  fontSize: T.fsSm5, fontWeight: T.fwSemibold
                }}
              >
                <Plus size={16} />
                New Objective
              </button>
            )}
          </div>
        </div>

         {data && lens !== "digest" && (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1.4fr) repeat(4, minmax(110px, 1fr))", gap: 10, marginBottom: 18 }}>
            <div style={{ padding: 18, background: T.dark, color: T.white, borderRadius: 8 }}>
              <div style={{ fontSize: T.fsXs, opacity: 0.72, textTransform: "uppercase", letterSpacing: "0.08em" }}>Overall rollup</div>
              <strong style={{ display: "block", fontSize: T.fsXl5, marginTop: 6 }}>{data.overall == null ? "—" : `${Math.round(data.overall)}%`}</strong>
              <span style={{ fontSize: T.fsXs, opacity: 0.72 }}>{data.eligibleKeyResultCount ?? 0} eligible KRs</span>
            </div>
            {[
              ["On track", data.statusCounts?.onTrack ?? 0, T.ragGreenText],
              ["At risk", data.statusCounts?.atRisk ?? 0, T.ragAmberText],
              ["Off track", data.statusCounts?.offTrack ?? 0, T.ragRed],
              ["Complete", data.statusCounts?.complete ?? 0, T.blue],
            ].map(([label, count, color]) => (
              <div key={label} style={{ padding: 18, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8 }}>
                <div style={{ fontSize: T.fsXs, color: T.mid, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                <strong style={{ display: "block", color, fontSize: T.fsXl, marginTop: 8 }}>{count}</strong>
                <span style={{ fontSize: T.fsXs, color: T.mid }}>key results</span>
              </div>
            ))}
          </div>
        )}

        {lens === "digest" ? (
           <OkrDigestView
            data={data}
            currentMonth={month}
            onMonthChange={handleMonthChange}
             year={year}
             loading={loading}
            currentUser={currentUser}
            currentRole={currentRole}
            onSaveUpdate={saveInitiativeUpdate}
          />
        ) : (
          <>
            {loading && !data ? (
              <div style={{ padding: 40, textAlign: "center", color: T.mid }}>Loading OKRs...</div>
            ) : data?.objectives?.length === 0 ? (
              <div style={{ padding: 60, textAlign: "center", background: T.card, borderRadius: 8, border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: T.fsMd5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 8 }}>No Objectives Found</div>
                <div style={{ fontSize: T.fsBase, color: T.mid, marginBottom: 24 }}>Get started by creating your first company objective.</div>
                {canEdit && (
                  <button
                    onClick={() => setObjModal({ open: true, data: null })}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "8px 16px", borderRadius: 6,
                      background: T.accent, color: T.white,
                      border: "none", cursor: "pointer",
                      fontSize: T.fsSm5, fontWeight: T.fwSemibold
                    }}
                  >
                    <Plus size={16} />
                    New Objective
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {data?.objectives?.map(obj => (
                  <OkrObjective
                    key={obj.id}
                    objective={obj}
                    keyResults={obj.keyResults}
                    canEdit={canEdit}
                    onEditObj={o => setObjModal({ open: true, data: o })}
                    onAddKR={o => setKrModal({ open: true, data: null, objectiveId: o.id })}
                    onEditKR={kr => setKrModal({ open: true, data: kr, objectiveId: kr.objectiveId })}
                    onAddInitiative={kr => setIniModal({ open: true, data: null, keyResultId: kr.id })}
                    onEditInitiative={ini => setIniModal({ open: true, data: ini, keyResultId: ini.keyResultId })}
                    onViewUpdates={ini => setUpdateModal({ open: true, initiative: ini })}
                  />
                ))}
              </div>
            )}
          </>
        )}

        <ObjectiveForm
          isOpen={objModal.open}
          onClose={() => setObjModal({ open: false, data: null })}
          initialData={objModal.data}
          selectedYear={year}
          onSubmit={handleObjSubmit}
        />
        <KeyResultForm
          isOpen={krModal.open}
          onClose={() => setKrModal({ open: false, data: null, objectiveId: null })}
          initialData={krModal.data}
          objectiveId={krModal.objectiveId}
          onSubmit={handleKrSubmit}
        />
        <InitiativeForm
          isOpen={iniModal.open}
          onClose={() => setIniModal({ open: false, data: null, keyResultId: null })}
          initialData={iniModal.data}
          keyResultId={iniModal.keyResultId}
          onSubmit={handleIniSubmit}
        />
        <OkrInitiativeUpdatesModal
          isOpen={updateModal.open}
          onClose={() => setUpdateModal({ open: false, initiative: null })}
          initiative={updateModal.initiative}
          selectedYear={year}
          initialMonth={month}
          currentUser={currentUser}
          currentRole={currentRole}
          onSave={async (id, updateMonth, payload) => {
             await saveInitiativeUpdate(id, updateMonth, payload);
          }}
        />
      </div>
    </ErrorBoundary>
  );
}
